/**
 * WaterfallResultsCard · Sprint 4 · v2.9.0
 *
 * Card que simula el waterfall multi-tranche contra el análisis más reciente
 * y muestra las distribuciones por Sponsor y cada LP con MOIC / profit / capital
 * devuelto / preferente recibido / catch-up + splits residuales tier 1 y 2.
 */
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { waterfallAPI } from '@/lib/api';
import { Users, TrendingUp, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';

function fmt(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

const TIER_LABEL = {
  return_of_capital: 'Devolución de capital',
  pref_return: 'Preferente',
  catch_up: 'Catch-up',
  residual_tier1: 'Split residual · Tier 1',
  residual_tier2_promote: 'Split residual · Tier 2 (promote)',
};

export function WaterfallResultsCard({ scenarioId, projectId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const simMut = useMutation({
    mutationFn: () => waterfallAPI.simulateV2({ scenario_id: scenarioId, project_id: projectId }),
    onSuccess: (r) => { setData(r.data); setErr(null); },
    onError: (e) => setErr(e?.response?.data?.detail || e?.message || 'Error'),
  });

  useEffect(() => {
    setData(null); setErr(null);
    if (scenarioId) simMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  if (simMut.isPending) return <div className="rounded-2xl border border-border/40 bg-card p-6 text-xs text-muted-foreground" data-testid="waterfall-card-loading">Simulando waterfall multi-tranche…</div>;

  if (err) {
    const msg = typeof err === 'string' ? err : (err.message || 'error');
    // No mostramos si no hay estructura configurada (comportamiento silencioso)
    if (typeof msg === 'string' && msg.toLowerCase().includes('estructura de capital')) return null;
    return (
      <div className="rounded-2xl border p-4 text-[11px]" style={{ borderColor: '#FCA5A5', background: '#FEF2F2', color: '#991B1B' }} data-testid="waterfall-card-error">
        Waterfall no ejecutado: {msg}
      </div>
    );
  }

  if (!data?.result) return null;
  const result = data.result;
  const ctx = data.context || {};
  const parties = result.party_metrics || {};
  const partiesArr = Object.entries(parties);
  const sponsor = parties._sponsor;
  const lps = partiesArr.filter(([id]) => id !== '_sponsor');
  const candados = result.candados || [];
  const allPassed = !!result.all_candados_passed;

  return (
    <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="waterfall-results-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#1E293B' }}>
            <Users className="inline w-4 h-4 mr-1" style={{ color: '#4F46E5' }} />
            Waterfall multi-tranche (Sprint 4 · v2.9.0)
          </h3>
          <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
            Distribución por parte · Métrica hurdle: <strong>{(result.metric_used_for_hurdle || 'mirr').toUpperCase()}</strong>
            {' · '}Pool distribuible: <strong>{fmt(result.distributable)}</strong>
            {' · '}Utilidad operativa: <strong>{fmt(ctx.total_profit)}</strong>
          </p>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-semibold uppercase" style={{ color: allPassed ? '#16A34A' : '#DC2626' }}>
          {allPassed ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
          Candados {allPassed ? 'OK' : 'con hallazgos'}
        </div>
      </div>

      {/* Party cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {[['_sponsor', sponsor], ...lps].filter(([, p]) => p).map(([pid, p]) => {
          const isSponsor = pid === '_sponsor';
          const moicColor = (p.moic || 0) >= 2 ? '#16A34A' : (p.moic || 0) >= 1.5 ? '#0369A1' : '#94A3B8';
          return (
            <div
              key={pid}
              className="rounded-xl border p-3"
              style={{
                borderColor: isSponsor ? 'rgba(79,70,229,0.35)' : 'rgba(3,105,161,0.30)',
                background: isSponsor ? 'rgba(79,70,229,0.03)' : 'rgba(3,105,161,0.03)',
              }}
              data-testid={`waterfall-party-${pid}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: isSponsor ? '#4F46E5' : '#0369A1' }}>
                  {p.name} {isSponsor ? '· Sponsor' : ''}
                </div>
                <div className="text-[10px] font-mono" style={{ color: moicColor }}>
                  {p.moic != null ? `${p.moic.toFixed(2)}x` : '—'}
                </div>
              </div>
              <div className="text-[10px] space-y-0.5" style={{ color: '#475569' }}>
                <Row label="Capital aportado" value={fmt(p.capital)} />
                <Row label="Capital devuelto" value={fmt(p.capital_returned)} />
                <Row label="Preferente recibido" value={`${fmt(p.pref_received)}  /  ${fmt(p.pref_target)} obj`} />
                {(p.catch_up_received || 0) > 0 && <Row label="Catch-up" value={fmt(p.catch_up_received)} />}
                <Row label="Residual T1" value={fmt(p.residual_tier1)} />
                {(p.residual_tier2 || 0) > 0 && <Row label="Residual T2 (promote)" value={fmt(p.residual_tier2)} />}
                <div className="pt-1 mt-1 border-t" style={{ borderColor: 'rgba(148,163,184,0.25)' }}>
                  <Row label="Total recibido" value={<strong>{fmt(p.total_received)}</strong>} strong />
                  <Row label="Utilidad" value={<strong style={{ color: (p.profit || 0) >= 0 ? '#16A34A' : '#DC2626' }}>{fmt(p.profit)}</strong>} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tiers ejecutados */}
      {(result.tiers || []).length > 0 && (
        <div className="rounded-lg border p-3 mb-3" style={{ borderColor: 'rgba(148,163,184,0.35)' }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
            <TrendingUp className="inline w-3 h-3 mr-1" />Tiers ejecutados
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
            {result.tiers.map((t, i) => (
              <div key={i} className="flex justify-between border-l-2 pl-2" style={{ borderColor: '#4F46E5' }}>
                <span style={{ color: '#334155' }}>{TIER_LABEL[t.tier] || t.tier}</span>
                <span style={{ color: '#4F46E5' }} className="font-mono">{fmt(t.allocated)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Candados */}
      {!allPassed && (
        <div className="rounded-lg border p-3" style={{ borderColor: '#FCA5A5', background: '#FEF2F2' }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#991B1B' }}>Candados con hallazgos</div>
          <div className="space-y-1 text-[10px]" style={{ color: '#7F1D1D' }}>
            {candados.filter(c => c.passed !== true && c.passed !== null).map((c, i) => (
              <div key={i}>[{c.id}] {c.name}</div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <Button size="sm" variant="ghost" onClick={() => simMut.mutate()} className="h-7 text-[11px]" data-testid="waterfall-rerun">Recalcular</Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
